import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { selectCurrentUser } from 'src/modules/users/selector';
import { SingleUserSelect } from '../../gos/components/SingleUserSelect/SingleUserSelect';
import InputDate from '../../gos/ui/input-date';
import Select from '../../vpo/ui/select';
import TextAreaVPO from '../../vpo/ui/textArea';
import { mockTypeAudit } from '../list5SAudit/filterData5SAudit';
import { fetchData } from '../store/filtersData/actions';
import { selectModelData } from '../store/filtersData/selector';
import { getAccessibleLocationIds } from '../utils/locationAccess';
import { stripPatronymic } from '../utils/userName';

export interface Add5SAuditMobilePredefined {
  typeAudit?: { id: number; name: string } | null;
  location?: { id: number; name: string } | null;
  department?: { id: number; name: string } | null;
  area?: { id: number; name: string } | null;
  zone?: { id: string; name: string } | null;
}

export interface Add5SAuditMobileHandle {
  submit: () => void;
}

interface Add5SAuditMobileProps {
  predefinedValues?: Add5SAuditMobilePredefined;
  editData?: any;
  readOnly?: boolean;
  onSubmitOverride?: (cardBody: any) => void;
  onValuesChange?: (values: any) => void;
}

const CHECKLIST_OPTIONS = [
  { id: 'Производственный', name: 'Производственный' },
  { id: 'Складской', name: 'Складской' },
  { id: 'Лабораторный', name: 'Лабораторный' },
];

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

function getTodayRange() {
  const t = todayISO();
  return { startDate: t, endDate: t };
}

const normalizeYo = (s: string | null | undefined) => (s ?? '').replace(/ё/gi, 'е');

function resolveChecklistType(
  typeAuditName: string | null | undefined,
  location: any,
  department: any,
  area: any,
  zoneName: string | null | undefined,
  depends: any[]
): string | null {
  if (!typeAuditName || !depends?.length) return null;
  const normalizedType = normalizeYo(typeAuditName);
  const match = depends.find((dep: any) => {
    const sameType = normalizeYo(dep.TypeAudit) === normalizedType;
    const sameLocation = dep.Location?.id === location?.id;
    const sameDept = dep.Department?.id === department?.id;
    const sameArea = dep.Area?.id === area?.id;
    const sameZone = !zoneName || !dep.Zone || dep.Zone === zoneName;
    return sameType && sameLocation && sameDept && sameArea && sameZone;
  });
  return match?.CheckList || null;
}

function mapStrapiUserToSelect(strapiUser: any) {
  if (!strapiUser) return null;
  const pn = strapiUser.PersonalNumber ? ` (${strapiUser.PersonalNumber})` : '';
  const displayName = stripPatronymic(strapiUser.DisplayName) || strapiUser.username || strapiUser.email || '';
  return {
    id: strapiUser.id,
    email: strapiUser.email,
    title: `${displayName}${pn}`,
    avatar: null,
    shortName: displayName.split(' ').filter(Boolean).map((w: string) => w[0]).join('').substring(0, 2).toUpperCase(),
  };
}

const auditMaxDate = new Date();
const auditMinDate = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d;
})();

const Add5SAuditMobile = forwardRef<Add5SAuditMobileHandle, Add5SAuditMobileProps>(function Add5SAuditMobile(
  { predefinedValues, editData, readOnly = false, onSubmitOverride, onValuesChange },
  ref
) {
  const dispatch = useAppDispatch();

  const FilterLocation = useAppSelector((state: any) => selectModelData(state, 'mt-locations'));
  const FilterDepartment = useAppSelector((state: any) => selectModelData(state, 'mt-departments'));
  const FilterArea = useAppSelector((state: any) => selectModelData(state, 'mt-areas'));
  const dependsInner = useAppSelector((state: any) => selectModelData(state, 'mt-5-s-depends'));
  const templates = useAppSelector((state: any) => selectModelData(state, 'mt-5-s-templates'));
  const currentUser: any = useAppSelector(selectCurrentUser);

  useEffect(() => {
    if (FilterLocation.length === 0) dispatch(fetchData('mt-locations', {}, { page: 1, pageSize: 100 }));
    if (FilterDepartment.length === 0) dispatch(fetchData('mt-departments', {}, { page: 1, pageSize: 100 }));
    if (FilterArea.length === 0) dispatch(fetchData('mt-areas', {}, { page: 1, pageSize: 200 }));
    if (dependsInner.length === 0) dispatch(fetchData('mt-5-s-depends', {}, { page: 1, pageSize: 100000 }));
    if (templates.length === 0) dispatch(fetchData('mt-5-s-templates', {}, { page: 1, pageSize: 100 }));
  }, [dispatch]);

  const currentUserAsSelect = useMemo(() => {
    if (!currentUser?.id) return null;
    return {
      id: currentUser.id,
      email: currentUser.email,
      title: stripPatronymic(currentUser.displayName) || currentUser.displayName,
      avatar: currentUser.avatar,
      shortName: currentUser.shortName,
    };
  }, [currentUser]);

  const editDefaultValues = useMemo(() => {
    if (!editData) return null;
    const typeAuditOption = mockTypeAudit.find((t) => normalizeYo(t.Title) === normalizeYo(editData.TypeAudit));
    const cardDate = editData.DateAudit || editData.PlannedDate;
    return {
      dateAudit: (readOnly || editData.isAudit) && cardDate ? { startDate: cardDate, endDate: cardDate } : getTodayRange(),
      typeAudit: typeAuditOption ? { id: typeAuditOption.ID, name: typeAuditOption.Title } : null,
      location: editData.Location ? { id: editData.Location.id, name: editData.Location.Title || editData.Location.name } : null,
      department: editData.Department ? { id: editData.Department.id, name: editData.Department.Title || editData.Department.name } : null,
      area: editData.Area ? { id: editData.Area.id, name: editData.Area.Title || editData.Area.name } : null,
      zone: editData.Zone ? { id: editData.Zone, name: editData.Zone } : null,
      auditor: mapStrapiUserToSelect(editData.Auditor),
      soauditor: mapStrapiUserToSelect(editData.CoAuditor),
      comment: editData.Comment || '',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData]);

  const methods = useForm<any>({
    defaultValues: editDefaultValues || {
      dateAudit: getTodayRange(),
      typeAudit: predefinedValues?.typeAudit || null,
      location: predefinedValues?.location || null,
      department: predefinedValues?.department || null,
      area: predefinedValues?.area || null,
      zone: predefinedValues?.zone || null,
      auditor: null,
      soauditor: null,
      comment: '',
    },
  });

  const { handleSubmit, control, watch, setValue, getValues } = methods;

  const selectedTypeAudit = watch('typeAudit');
  const selectedLocation = watch('location');
  const selectedDepartment = watch('department');
  const selectedArea = watch('area');
  const selectedZone = watch('zone');

  const isCrossAudit = selectedTypeAudit?.id === 2;
  const planFieldsDisabled = !!editData;

  const defaultLocation = useMemo(() => {
    if (!currentUser || FilterLocation.length === 0) return null;
    const ids = [
      ...(Array.isArray(currentUser.isLocationAdminMyTasks) ? currentUser.isLocationAdminMyTasks : []),
      ...(Array.isArray(currentUser.isLocationEditorMyTasks) ? currentUser.isLocationEditorMyTasks : []),
    ];
    const loc = FilterLocation
      .filter((l: any) => ids.includes(l.id))
      .sort((a: any, b: any) => a.id - b.id)[0];
    return loc ? { id: loc.id, name: loc.Title } : null;
  }, [FilterLocation, currentUser]);

  const defaultDepartment = useMemo(() => {
    if (!defaultLocation || FilterDepartment.length === 0 || dependsInner.length === 0 || !currentUser?.department) return null;
    const availableDeptIds = new Set(
      dependsInner
        .filter((dep: any) => dep.Location?.id === defaultLocation.id)
        .map((dep: any) => dep.Department?.id)
        .filter(Boolean)
    );
    const dept = FilterDepartment.find((d: any) =>
      d.User_Department && d.User_Department.indexOf(currentUser.department) !== -1 && availableDeptIds.has(d.id)
    );
    return dept ? { id: dept.id, name: dept.Title } : null;
  }, [defaultLocation, FilterDepartment, dependsInner, currentUser]);

  useEffect(() => {
    if (editData) return;
    if (defaultLocation && !getValues('location')) setValue('location', defaultLocation);
  }, [defaultLocation, editData]);

  useEffect(() => {
    if (editData) return;
    if (defaultDepartment && !getValues('department') && getValues('location')?.id === defaultLocation?.id) {
      setValue('department', defaultDepartment);
    }
  }, [defaultDepartment, editData]);

  useEffect(() => {
    if (editData) return;
    if (currentUserAsSelect && !getValues('auditor')) {
      setValue('auditor', currentUserAsSelect);
    }
  }, [currentUserAsSelect, editData]);

  const checkListType = useMemo(
    () => resolveChecklistType(
      selectedTypeAudit?.name,
      selectedLocation,
      selectedDepartment,
      selectedArea,
      typeof selectedZone === 'string' ? selectedZone : selectedZone?.name,
      dependsInner
    ),
    [selectedTypeAudit, selectedLocation, selectedDepartment, selectedArea, selectedZone, dependsInner]
  );

  const checkListValue = checkListType ? { id: checkListType, name: checkListType } : null;

  const matchedTemplate = useMemo(() => {
    if (!checkListType || !selectedTypeAudit?.name) return null;
    const normalizedType = normalizeYo(selectedTypeAudit.name);
    return templates.find(
      (t: any) => t.ChecklistType === checkListType && normalizeYo(t.AuditType) === normalizedType
    ) || null;
  }, [templates, checkListType, selectedTypeAudit]);

  const filteredOptions = useMemo(() => {
    const normSelType = selectedTypeAudit ? normalizeYo(selectedTypeAudit.name) : null;

    let depsForType = dependsInner;
    if (selectedLocation) depsForType = depsForType.filter((dep: any) => dep.Location?.id === selectedLocation.id);
    if (selectedDepartment) depsForType = depsForType.filter((dep: any) => dep.Department?.id === selectedDepartment.id);
    if (selectedArea) depsForType = depsForType.filter((dep: any) => dep.Area?.id === selectedArea.id);
    const availableTypeNames = new Set(depsForType.map((dep: any) => normalizeYo(dep.TypeAudit)).filter(Boolean));
    const types = mockTypeAudit
      .filter((t) => availableTypeNames.size === 0 || availableTypeNames.has(normalizeYo(t.Title)))
      .map((t) => ({ id: t.ID, name: t.Title }));

    let filteredDeps = dependsInner;

    if (normSelType) {
      filteredDeps = filteredDeps.filter((dep: any) => normalizeYo(dep.TypeAudit) === normSelType);
    }
    if (selectedLocation) {
      filteredDeps = filteredDeps.filter((dep: any) => dep.Location?.id === selectedLocation.id);
    }

    const availableDeptIds = new Set(filteredDeps.map((dep: any) => dep.Department?.id).filter(Boolean));
    const departments = selectedLocation || normSelType
      ? FilterDepartment.filter((d: any) => availableDeptIds.has(d.id))
      : FilterDepartment;

    if (selectedDepartment) {
      filteredDeps = filteredDeps.filter((dep: any) => dep.Department?.id === selectedDepartment.id);
    }

    const availableAreaIds = new Set(filteredDeps.map((dep: any) => dep.Area?.id).filter(Boolean));
    const areas = selectedLocation || selectedDepartment || normSelType
      ? FilterArea.filter((a: any) => availableAreaIds.has(a.id))
      : FilterArea;

    if (selectedArea) {
      filteredDeps = filteredDeps.filter((dep: any) => dep.Area?.id === selectedArea.id);
    }

    const availableZones = new Set(filteredDeps.map((dep: any) => dep.Zone).filter(Boolean));
    const zones = [...availableZones].map((name) => ({ id: name, name: name as string }));

    const accessibleIds = getAccessibleLocationIds(currentUser);
    const isAllAccess = accessibleIds === 'all';

    return {
      types,
      locations: FilterLocation
        .filter((loc: any) => isAllAccess || (accessibleIds as number[]).includes(loc.id))
        .map((loc: any) => ({ id: loc.id, name: loc.Title })),
      departments: departments.map((d: any) => ({ id: d.id, name: d.Title })),
      areas: areas.map((a: any) => ({ id: a.id, name: a.Title })),
      zones,
    };
  }, [selectedTypeAudit, selectedLocation, selectedDepartment, selectedArea, dependsInner, FilterLocation, FilterDepartment, FilterArea, currentUser]);

  useEffect(() => {
    if (editData) return;
    if (selectedTypeAudit) {
      const typeExists = filteredOptions.types.some((t: any) => t.id === selectedTypeAudit.id);
      if (!typeExists) setValue('typeAudit', null);
    }
  }, [selectedLocation, selectedDepartment, selectedArea]);

  useEffect(() => {
    if (editData) return;
    if (selectedDepartment) {
      const deptExists = filteredOptions.departments.some((d: any) => d.id === selectedDepartment.id);
      if (!deptExists) setValue('department', null);
    }
  }, [selectedLocation, selectedTypeAudit]);

  useEffect(() => {
    if (editData) return;
    if (selectedArea) {
      const areaExists = filteredOptions.areas.some((a: any) => a.id === selectedArea.id);
      if (!areaExists) setValue('area', null);
    }
  }, [selectedDepartment, selectedTypeAudit]);

  useEffect(() => {
    if (editData) return;
    if (selectedZone) {
      const zoneExists = filteredOptions.zones.some((z: any) => z.id === (selectedZone.id || selectedZone));
      if (!zoneExists) setValue('zone', null);
    }
  }, [selectedArea, selectedTypeAudit]);

  useEffect(() => {
    if (editData) return;
    if (isCrossAudit) {
      setValue('zone', null);
    }
  }, [isCrossAudit, editData]);

  useEffect(() => {
    if (!onValuesChange) return;
    const subscription = methods.watch((value) => onValuesChange(value));
    return () => subscription.unsubscribe();
  }, [methods, onValuesChange]);

  const onSubmit = (data: any) => {
    const titleParts = [
      data.dateAudit?.startDate,
      data.location?.name,
      data.area?.name,
      data.zone?.name,
      data.typeAudit?.name,
    ].filter(Boolean);

    const requestBody: any = {
      Title: titleParts.join(' · '),
      DateAudit: data.dateAudit?.startDate || null,
      TypeAudit: data.typeAudit?.name || null,
      Location: data.location?.id || null,
      Department: data.department?.id || null,
      Area: data.area?.id || null,
      Zone: data.zone?.name || null,
      Auditor: data.auditor?.id || null,
      CoAuditor: data.soauditor?.id || null,
      Comment: data.comment || null,
      isAudit: true,
    };

    if (!editData?.isAudit) {
      requestBody.PlannedDate = data.dateAudit?.startDate || null;
    }

    if (matchedTemplate?.id) requestBody.Template = matchedTemplate.id;

    onSubmitOverride?.(requestBody);
  };

  useImperativeHandle(ref, () => ({
    submit: () => handleSubmit(onSubmit)(),
  }), [handleSubmit, onSubmit]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='flex flex-col gap-5'>
          <Controller
            control={control}
            name='dateAudit'
            render={({ field: { onChange, value } }) => (
              <InputDate
                label='Дата факт'
                value={value}
                onChange={onChange}
                disabled={readOnly}
                minDate={auditMinDate}
                maxDate={auditMaxDate}
                required
              />
            )}
          />
          <Controller
            rules={{ required: true }}
            control={control}
            name='typeAudit'
            render={({ field: { onChange, value } }) => (
              <Select data={filteredOptions.types} selected={value} onChange={onChange} label='Тип аудита' disabled={planFieldsDisabled} placeholder='Выберите значение' required width='w-full' />
            )}
          />
          <Controller
            rules={{ required: true }}
            control={control}
            name='location'
            render={({ field: { onChange, value } }) => (
              <Select data={filteredOptions.locations} selected={value} onChange={onChange} label='Локация' disabled={planFieldsDisabled} placeholder='Выберите локацию' required width='w-full' />
            )}
          />
          <Controller
            rules={{ required: true }}
            control={control}
            name='department'
            render={({ field: { onChange, value } }) => (
              <Select data={filteredOptions.departments} selected={value} onChange={onChange} label='Подразделение' disabled={planFieldsDisabled} placeholder='Выберите подразделение' required width='w-full' />
            )}
          />
          <Controller
            rules={{ required: true }}
            control={control}
            name='area'
            render={({ field: { onChange, value } }) => (
              <Select data={filteredOptions.areas} selected={value} onChange={onChange} label='Участок' disabled={planFieldsDisabled} placeholder='Выберите участок' required width='w-full' />
            )}
          />
          {!isCrossAudit && (
            <Controller
              rules={{ required: true }}
              control={control}
              name='zone'
              render={({ field: { onChange, value } }) => (
                <Select data={filteredOptions.zones} selected={value} onChange={onChange} label='Зона' disabled={planFieldsDisabled} placeholder='Выберите зону' required width='w-full' />
              )}
            />
          )}
          <Select
            data={CHECKLIST_OPTIONS}
            selected={checkListValue}
            onChange={() => {}}
            label='Чек-лист'
            placeholder='—'
            required
            disabled
            width='w-full'
          />
          <Controller
            control={control}
            name='auditor'
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <SingleUserSelect selectedUser={value} onUserSelect={onChange} disabled={readOnly} label='Аудитор' required />
            )}
          />
          <Controller
            control={control}
            name='soauditor'
            render={({ field: { onChange, value } }) => (
              <SingleUserSelect selectedUser={value} onUserSelect={onChange} disabled={readOnly} label='Соаудитор' />
            )}
          />
          <Controller
            control={control}
            name='comment'
            render={({ field }) => (
              <TextAreaVPO
                dataField={{
                  type: 'textArea',
                  require: false,
                  width: 'w-full',
                  title: 'Комментарий',
                  key: 'comment',
                  placeholder: 'Введите комментарий',
                  disabled: planFieldsDisabled,
                }}
                value={field.value}
                onChange={field.onChange}
                errors={{}}
                minHeight={96}
                disabled={planFieldsDisabled}
              />
            )}
          />
        </div>
      </form>
    </FormProvider>
  );
});

export default Add5SAuditMobile;
